import { Box, CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { isNil } from "lodash";
import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

import { activityApi } from "../../../../apis";
import {
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDto,
  MetricValuesPaginationRequestDtoHealthProviderCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "../../../../libs/cellera-sdk";
import LayoutWrapper from "../LayoutWrapper";
import { MetricCard, MetricData, SectionWrapper } from "./index";

interface ReproductiveHealthProps {
  patientId: string;
}

const getFallbackInfo = (): MetricData["fallbackInfo"] => ({
  message: "No recent data available",
  suggestion: "Consider manual measurement",
  lastRecorded: "Not available",
});

const ReproductiveHealth: React.FC<ReproductiveHealthProps> = ({
  patientId,
}) => {
  const { colors } = useCelleraTheme();
  const { data: reproductiveHealthData, isLoading } = useQuery({
    queryKey: ["reproductive-health", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      const request: MetricValuesPaginationRequestDto = {
        healthProviderCode:
          MetricValuesPaginationRequestDtoHealthProviderCodeEnum.GoogleHealthConnect,
        metricCode: MetricValuesPaginationRequestDtoMetricCodeEnum.Menstruation,
        page: 1,
        pageSize: 1,
        orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
      };

      return activityApi.getMetricValuesPaginatedByPatientId({
        patientId,
        metricValuesPaginationRequestDto: request,
      });
    },
    enabled: !!patientId,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();

  const latestValue = useMemo(
    () => reproductiveHealthData?.data?.[0],
    [reproductiveHealthData]
  );

  return (
    <SectionWrapper
      title="Reproductive Health"
      spacing="normal"
      sx={{ pb: 2, pt: 2 }}
    >
      <LayoutWrapper itemsPerRow={1}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: colors.primary.main }} />
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Loading reproductive health data...
            </Typography>
          </Box>
        ) : (
          <MetricCard
            key={MetricValuesPaginationRequestDtoMetricCodeEnum.Menstruation}
            metric={{
              id: MetricValuesPaginationRequestDtoMetricCodeEnum.Menstruation,
              name: "Reproductive Health",
              hasData: !isNil(latestValue),
              startEndDate: {
                startDate: latestValue?.startDate
                  ? new Date(latestValue.startDate).toLocaleString()
                  : "Never",
                endDate: latestValue?.endDate
                  ? new Date(latestValue.endDate).toLocaleString()
                  : "Never",
              },
              lastChecked: latestValue?.createdAt
                ? new Date(latestValue.createdAt).toLocaleString()
                : "Never",
              fallbackInfo: getFallbackInfo(),
            }}
            onNext={() => navigate(`/profile/reproductive-health/${patientId}`)}
          />
        )}
      </LayoutWrapper>
    </SectionWrapper>
  );
};

export default ReproductiveHealth;
